import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import Header from "@/components/Header";
import BottomBar from "@/components/BottomBar";
import {
  ExternalLink,
  Loader2,
  Shield,
  Camera,
  FileText,
  AlertCircle,
} from "lucide-react";
import { oxpayAPI } from "@/lib/oxpay";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "react-toastify";

export default function KycOnfido() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [onfidoData, setOnfidoData] = useState(null);
  const [accountData, setAccountData] = useState(null);
  const [kycUrl, setKycUrl] = useState(null);
  const [kycError, setKycError] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [kycStatus, setKycStatus] = useState(null);
  const [poaDocumentUrl, setPoaDocumentUrl] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [hasCheckedStatus, setHasCheckedStatus] = useState(false);

  useEffect(() => {
    fetchAccountData();
  }, [user?.email]);

  // Auto-check KYC status when account data is loaded
  useEffect(() => {
    if (accountData?.oxpay_user_id && !hasCheckedStatus) {
      setHasCheckedStatus(true);
      checkKycStatus();
    }
  }, [accountData?.oxpay_user_id, hasCheckedStatus]);

  const fetchAccountData = async () => {
    if (!user?.email) return;

    try {
      const { data, error } = await supabase
        .from("accounts")
        .select("*")
        .eq("email", user.email)
        .single();

      if (data && !error) {
        setAccountData(data);
      }
    } catch (error) {
      console.error("Error fetching account data:", error);
    }
  };

  const checkKycStatus = async () => {
    setCheckingStatus(true);
    setKycError(null);

    try {
      // Check if user has oxpay_user_id
      if (!accountData?.oxpay_user_id) {
        setKycError("Please complete user registration first");
        setTimeout(() => navigate("/register-user"), 2000);
        return;
      }

      // Get KYC status from OxPay
      const response = await oxpayAPI.getKYCStatus(accountData.oxpay_user_id);
      console.log("KYC Status response:", response);

      if (response?.body) {
        const { status } = response.body;
        setKycStatus(status);

        // Check KYC status - [not_submitted, pending, submitted, under_review, approved, rejected]
        if (status === "approved") {
          // KYC 승인됨
          await supabase
            .from("accounts")
            .update({
              kyc_status: "approved",
              // kyc_approved_at: new Date().toISOString(),
              // kyc_level: response.body.kyc_level || 1,
              poa_status: "approved",
              // poa_approved_at: new Date().toISOString(),
            })
            .eq("email", user?.email);

          toast.success("KYC verification already completed!");
          setTimeout(() => navigate("/kyc-complete"), 2000);
          return;
        } else if (
          status === "pending" ||
          status === "submitted" ||
          status === "under_review"
        ) {
          // KYC 진행 중
          toast.info(
            "KYC verification is in progress. Please complete the verification process."
          );

          // If there's an existing KYC URL, use it
          if (
            response.body.links?.[0]?.href &&
            response.body.links?.[0]?.href_poa
          ) {
            setKycUrl(response.body.links[0].href);
            setPoaDocumentUrl(response.body.links[0].href_poa);
            setOnfidoData(response.body);
          } else {
            // Start new KYC process if no URL available
            startKycProcess();
          }
        } else if (status === "rejected") {
          // KYC 거절됨
          await supabase
            .from("accounts")
            .update({
              kyc_status: "rejected",
              kyc_rejected_at: new Date().toISOString(),
              kyc_rejection_reason:
                response.body.rejection_reason || "Verification failed",
            })
            .eq("email", user?.email);

          setKycError("KYC verification was rejected. Please try again.");
          // Allow user to restart KYC process
          setTimeout(() => startKycProcess(), 3000);
        } else if (status === "not_submitted") {
          // KYC 시작 안됨
          startKycProcess();
        } else {
          // 알 수 없는 상태 - 새로 시작
          startKycProcess();
        }
      } else {
        // No KYC status found, start new process
        // startKycProcess();
      }
    } catch (error) {
      console.error("Error checking KYC status:", error);
      // If error checking status, start new KYC process
      // startKycProcess();
    } finally {
      setCheckingStatus(false);
    }
  };

  const startKycProcess = async () => {
    setIsLoading(true);
    setKycError(null);

    try {
      // Check if user has oxpay_user_id
      if (!accountData?.oxpay_user_id) {
        setKycError("Please complete user registration first");
        setTimeout(() => navigate("/register-user"), 2000);
        return;
      }

      // Check if user has required fields (title)
      // if (!accountData?.title) {
      //   setKycError("Please update your profile with title information");
      //   setTimeout(() => navigate("/register-user"), 2000);
      //   return;
      // }

      // Call OxPay API to start KYC
      const response = await oxpayAPI.startKYC(accountData.oxpay_user_id);

      if (response?.body?.links[0]?.href && response.body.links[0].href_poa) {
        // Store Onfido data
        setOnfidoData(response.body);
        const onfidoUrl = response.body.links[0].href;
        setKycUrl(onfidoUrl);
        console.log("KYC URL received:", onfidoUrl);

        // Set POA URL if available
        if (response.body.links[0].href_poa) {
          setPoaDocumentUrl(response.body.links[0].href_poa);
          console.log("POA URL received:", response.body.links[0].href_poa);
        }

        // Update KYC status in database
        await supabase
          .from("accounts")
          .update({
            kyc_status: "pending",
            // kyc_submitted_at: new Date().toISOString(),
            poa_status: "pending",
            // poa_submitted_at: new Date().toISOString(),
            poa_document_url: response.body.links[0].href_poa,
          })
          .eq("email", user?.email);

        toast.success(
          "KYC verification ready! Click the button below to start."
        );
      } else {
        // Fallback for development/testing
        console.log("KYC Response:", response);
        setKycError("Unable to get verification URL. Please try again.");
      }
    } catch (error) {
      // check already completed
      if (
        error.message ===
        "The profile has already completed the kyc verification"
      ) {
        // KYC 승인됨
        await supabase
          .from("accounts")
          .update({
            kyc_status: "approved",
            // kyc_approved_at: new Date().toISOString(),
            // kyc_level: response.body.kyc_level || 1,
            poa_status: "approved",
            // poa_approved_at: new Date().toISOString(),
          })
          .eq("email", user?.email);

        toast.success("KYC verification already completed!");
        setTimeout(() => navigate("/kyc-complete"), 2000);
        return;
      }

      console.error("KYC start error:", error?.message || error);
      setKycError(error.message || "Failed to start KYC verification");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenKyc = () => {
    if (kycUrl) {
      window.open(kycUrl, "_blank", "noopener,noreferrer");
    }
  };

  const handleCopyLink = async () => {
    if (kycUrl) {
      try {
        await navigator.clipboard.writeText(kycUrl);
        setCopySuccess(true);
        toast.success("Link copied to clipboard!");
        setTimeout(() => setCopySuccess(false), 3000);
      } catch (err) {
        toast.error("Failed to copy link");
      }
    }
  };

  const handleOpenPoa = () => {
    if (poaDocumentUrl) {
      window.open(poaDocumentUrl, "_blank", "noopener,noreferrer");
    }
  };

  const handleCopyPoaLink = async () => {
    if (poaDocumentUrl) {
      try {
        await navigator.clipboard.writeText(poaDocumentUrl);
        setCopySuccess(true);
        toast.success("POA link copied to clipboard!");
        setTimeout(() => setCopySuccess(false), 3000);
      } catch (err) {
        toast.error("Failed to copy POA link");
      }
    }
  };

  const handleRetry = () => {
    setKycError(null);
    setKycUrl(null);
    startKycProcess();
  };

  return (
    <div className="min-h-screen bg-background">
      <Header showBack={true} onBackClick={() => navigate("/home")} />

      <div className="max-w-md mx-auto px-3 pb-20">
        {/* Title */}
        <div className="mt-6 mb-6">
          <h1 className="text-2xl font-bold">Verify Your Identity</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Complete verification with our trusted partner
          </p>
        </div>

        {/* What You'll Need Card */}
        <Card className="bg-card">
          <CardContent className="px-6 pt-4">
            <h3 className="font-medium mb-4 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              What you'll need
            </h3>
            <div className="space-y-2">
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 bg-primary rounded-full mt-1.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium">Government-issued ID</p>
                  <p className="text-xs text-muted-foreground">
                    Passport or national ID card
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 bg-primary rounded-full mt-1.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium">Clear selfie</p>
                  <p className="text-xs text-muted-foreground">
                    We'll guide you through the process
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 bg-primary rounded-full mt-1.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium">Proof of address</p>
                  <p className="text-xs text-muted-foreground">
                    Bank statement or utility bill (within 3 months)
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Verification Process Card */}
        {/* <Card className="bg-card mt-4">
          <CardContent className="p-6">
            <h3 className="font-medium mb-4 flex items-center gap-2">
              <Camera className="w-4 h-4" />
              Quick verification process
            </h3>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-primary">
                  Step 1:
                </span>
                <span className="text-xs text-muted-foreground">
                  Photograph your documents
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-primary">
                  Step 2:
                </span>
                <span className="text-xs text-muted-foreground">
                  Take a selfie for biometric check
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-primary">
                  Step 3:
                </span>
                <span className="text-xs text-muted-foreground">
                  Instant verification results
                </span>
              </div>
            </div>
          </CardContent>
        </Card> */}

        {/* Security Notice */}
        <Card className="bg-muted/50 mt-4">
          <CardContent className="p-4">
            <div className="flex items-start gap-2">
              <Shield className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
              <div className="text-xs">
                <p className="font-medium mb-1">Your data is secure</p>
                <p className="text-muted-foreground">
                  Verification is handled by Onfido, a certified identity
                  verification provider. Your data is encrypted and never
                  shared.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* External Link Notice */}
        <div className="flex items-center gap-2 mt-4 px-1">
          <ExternalLink className="w-3 h-3 text-muted-foreground" />
          <p className="text-xs text-muted-foreground">
            You'll be redirected to a secure verification page
          </p>
        </div>

        {/* Action Buttons */}
        <div className="mt-8 space-y-3">
          {/* Checking Status */}
          {checkingStatus && (
            <div className="flex items-center justify-center p-4">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              <span>Checking KYC status...</span>
            </div>
          )}

          {/* Loading State */}
          {isLoading && !checkingStatus && (
            <div className="flex items-center justify-center p-4">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              <span>Starting KYC verification...</span>
            </div>
          )}

          {/* Error State */}
          {kycError && (
            <div className="space-y-3">
              <Alert className="border-red-600 bg-red-50 dark:bg-red-950/20">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800 dark:text-red-200">
                  {kycError}
                </AlertDescription>
              </Alert>
              <div className="flex gap-2">
                <Button
                  onClick={handleRetry}
                  className="flex-1"
                  variant="default"
                >
                  Try Again
                </Button>
                <Button
                  onClick={() => navigate("/home")}
                  className="flex-1"
                  variant="outline"
                >
                  Go Back
                </Button>
              </div>
            </div>
          )}

          {/* Success State with KYC URL */}
          {kycUrl && !kycError && (
            <div className="space-y-3">
              {/* POA Document Buttons */}
              {poaDocumentUrl && (
                <div className="mt-4 space-y-3 border-t pt-4">
                  <p className="text-sm font-medium text-center">
                    Proof of Address Document
                  </p>

                  <Button
                    onClick={handleOpenPoa}
                    className="w-full h-12 text-base font-semibold"
                    size="lg"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Submit POA Document
                  </Button>

                  {/* <Button
                    onClick={handleCopyPoaLink}
                    variant="outline"
                    className="w-full"
                  >
                    <svg
                      className="w-4 h-4 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                    Copy POA Link
                  </Button> */}
                </div>
              )}

              {/* KYC Document Buttons */}
              <Button
                onClick={handleOpenKyc}
                className="w-full h-12 text-base font-semibold"
                size="lg"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open KYC Verification
              </Button>

              {/* <Button
                onClick={handleCopyLink}
                variant="outline"
                className="w-full"
              >
                {copySuccess ? (
                  <>
                    <svg
                      className="w-4 h-4 mr-2 text-green-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    Link Copied!
                  </>
                ) : (
                  <>
                    <svg
                      className="w-4 h-4 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                    Copy KYC Link
                  </>
                )}
              </Button> */}

              <p className="text-xs text-center text-muted-foreground">
                If the button doesn't work, copy the link and paste it in a new
                browser tab
              </p>
            </div>
          )}
        </div>

        {/* Progress Indicator */}
        {/* <div className="flex gap-1 justify-center mt-8">
          <div className="w-20 h-2 bg-primary rounded-sm" />
          <div className="w-20 h-2 bg-muted rounded-sm" />
          <div className="w-20 h-2 bg-muted rounded-sm" />
          <div className="w-20 h-2 bg-muted rounded-sm" />
        </div> */}
      </div>

      <BottomBar />
    </div>
  );
}
